import React, { useEffect, useState } from "react";
import api, { formatApiError, asArray } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY = { title_ar: "", title_en: "", body_ar: "", body_en: "", icon: "Plus", order: 0, published: true };
const inputCls = "w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2";
const textareaCls = "w-full bg-white border border-black/15 focus:border-[#0060A7] outline-none p-3 text-sm leading-relaxed";

// Most common lucide icons used for "values" — admin can type any other lucide name freely
const ICON_HINTS = ["ShieldCheck", "Handshake", "Zap", "Eye", "Compass", "Search", "Target", "Heart", "Star", "Award", "Flag", "Sparkles"];

export default function AdminValues() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/values").then((r) => setItems(asArray(r.data, "values"))).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            if (editing.id) await api.patch(`/admin/values/${editing.id}`, stripMeta(editing));
            else await api.post("/admin/values", stripMeta(editing));
            setEditing(null); load();
            toast.success("تم الحفظ");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (id) => {
        if (!window.confirm("هل أنت متأكد من حذف هذه القيمة؟")) return;
        await api.delete(`/admin/values/${id}`);
        load(); toast.success("تم الحذف");
    };

    return (
        <div data-testid="admin-values-page" className="max-w-5xl">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="heading text-2xl tracking-tight font-bold">قيمنا (About)</h1>
                    <p className="text-sm text-[#6B7280] mt-1">إدارة القيم الستة المعروضة في صفحة "عن ليجيندز".</p>
                </div>
                <button
                    onClick={() => setEditing({ ...EMPTY, order: items.length })}
                    data-testid="values-new-btn"
                    className="bg-[#002951] hover:bg-[#0060A7] text-white px-5 py-2.5 text-sm uppercase tracking-[0.2em] inline-flex items-center gap-2"
                >
                    <Plus size={14} /> قيمة جديدة
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((v) => (
                    <div key={v.id} className="bg-white p-6 border border-black/5" data-testid={`value-row-${v.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-1">#{v.order + 1} · {v.icon}</div>
                                <div className="heading text-lg font-bold">{v.title_ar}</div>
                                <div className="text-xs text-[#6B7280]">{v.title_en}</div>
                                <p className="text-sm text-[#374151] mt-2 line-clamp-2">{v.body_ar}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => setEditing(v)} className="p-2 hover:bg-black/5" data-testid={`value-edit-${v.id}`}><Pencil size={14} /></button>
                                <button onClick={() => del(v.id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`value-delete-${v.id}`}><Trash2 size={14} /></button>
                            </div>
                        </div>
                        {!v.published && <div className="mt-3 inline-block text-[10px] uppercase tracking-[0.2em] text-[#0060A7] bg-blue-50 px-2 py-1">مخفي</div>}
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="value-edit-modal">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="heading text-xl font-bold">{editing.id ? "تعديل قيمة" : "قيمة جديدة"}</h2>
                            <button onClick={() => setEditing(null)} className="p-2 hover:bg-black/5"><X size={16} /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Title (AR)</label>
                                <input className={inputCls} value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} data-testid="value-title-ar" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Title (EN)</label>
                                <input className={inputCls} value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="value-title-en" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Body (AR)</label>
                                <textarea rows={3} className={textareaCls} value={editing.body_ar} onChange={(e) => setEditing({ ...editing, body_ar: e.target.value })} data-testid="value-body-ar" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Body (EN)</label>
                                <textarea rows={3} className={textareaCls} value={editing.body_en} onChange={(e) => setEditing({ ...editing, body_en: e.target.value })} data-testid="value-body-en" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Lucide Icon Name</label>
                                <input list="icon-hints" className={inputCls} value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} data-testid="value-icon" />
                                <datalist id="icon-hints">{ICON_HINTS.map((i) => <option key={i} value={i} />)}</datalist>
                                <div className="text-[10px] text-[#6B7280] mt-1">اسم أيقونة من lucide-react. اقتراحات: {ICON_HINTS.slice(0, 6).join(", ")}</div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Order</label>
                                <input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })} />
                            </div>
                            <label className="md:col-span-2 inline-flex items-center gap-3 text-sm">
                                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="value-published" />
                                نشر القيمة على الموقع
                            </label>
                        </div>
                        <div className="mt-8 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm uppercase tracking-[0.2em] border border-black/15">إلغاء</button>
                            <button onClick={save} data-testid="value-save-btn" className="bg-[#002951] hover:bg-[#0060A7] text-white px-6 py-2.5 text-sm uppercase tracking-[0.2em]">حفظ</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function stripMeta(v) {
    const { id, created_at, ...rest } = v;
    return rest;
}
